import type { SidekickError } from '../state/chatTypes';

/**
 * Wire shape of a BE error body. Handles both the flat
 * `Error`-shape (`{ code, message, ... }`) and the `{ error: Error }`
 * envelope shape, since the gateway has not been observed in both
 * yet and both are valid for proto-defined error responses.
 */
export type WireErrorBody = {
  code?: string;
  message?: string;
  requestId?: string;
  retryable?: boolean;
  retryAfterSeconds?: number;
  error?: {
    code?: string;
    message?: string;
    requestId?: string;
    retryable?: boolean;
    retryAfterSeconds?: number;
  };
};

/**
 * Reads a non-2xx response body and normalizes it into the FE
 * `SidekickError` shape. Tolerates both the flat proto-`Error` body
 * and the `{ error: ... }` envelope (see {@link WireErrorBody}).
 * When the body cannot be parsed at all, falls back to a synthetic
 * `HTTP_<status>` code so the orchestrator still has a typed
 * classifier input.
 */
export const parseErrorBody = async (
  response: Response
): Promise<SidekickError> => {
  let parsed: WireErrorBody | null = null;
  try {
    parsed = (await response.json()) as WireErrorBody;
  } catch {
    parsed = null;
  }
  const envelope = parsed?.error;
  const code = envelope?.code ?? parsed?.code ?? `HTTP_${response.status}`;
  const message =
    envelope?.message ??
    parsed?.message ??
    `Sidekick request failed (status ${response.status})`;
  const requestId = envelope?.requestId ?? parsed?.requestId;
  const retryable = envelope?.retryable ?? parsed?.retryable;
  const retryAfterSeconds =
    envelope?.retryAfterSeconds ?? parsed?.retryAfterSeconds;
  return {
    code,
    message,
    ...(requestId !== undefined && requestId !== '' ? { requestId } : {}),
    ...(typeof retryable === 'boolean' ? { retryable } : {}),
    ...(typeof retryAfterSeconds === 'number' ? { retryAfterSeconds } : {}),
  };
};
